import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { resolveBrandFontFamilies } from '@src/utility/fonts';
import { useMemo } from 'react';

import type { FontFamily } from '@src/types.ts/story';

/**
 * Brand-kit font families visible in editor surfaces — archived variants and
 * fully-archived families stripped, with the legacy flat `customFonts[]`
 * grouped as a fallback when no structured families are stored yet.
 *
 * Reads the current org branding from the store and memoises on the two raw
 * branding fields the resolution depends on. Call from an `observer` component
 * so branding mutations trigger a re-render (and thus a fresh resolution).
 *
 * Replaces a ~10-line `useMemo` that had been copy-pasted (and started to
 * drift) across BrandKitFonts, FontFamilyVariantDropdown, TextFontPicker and
 * TextFloatingToolbar. The pure resolver lives in `@src/utility/fonts`
 * (`resolveBrandFontFamilies`) and is unit-tested there.
 */
export const useBrandFontFamilies = (): FontFamily[] => {
  const videoCreator = useVideoCreatorStore();
  const branding = videoCreator.organization?.branding;
  // Depend on the raw fields the resolver reads, not the `branding` object
  // identity (which MobX may hand back fresh on unrelated changes).
  return useMemo(
    () => resolveBrandFontFamilies(branding),
    [branding?.fontFamilies, branding?.customFonts],
  );
};

export default useBrandFontFamilies;
